import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  ManageNotificationsApiElementContentModel,
  NotificationPreferenceDto,
  NotificationTypeContentResponseModel,
  UpdateNotificationPreferenceRequest,
} from '@/services/platform/api/model';
import { cn, LinksToLinkMap, ModuleProperties, useClientTranslation } from '@/lib/core';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { useLocaleStore, useUIStore } from '@/lib/core/stores';
import {
  enableAllNotificationCookies,
  getNotificationTypes,
  getProfileNotifications,
  updateProfileNotifications,
} from '@/services/platform/data/profile/notifications';
import {
  pushNotificationIsSkippedOrNotAllowed,
  requestPushNotificationPermission,
} from '@/lib/notifications/pushNotifications';
import { getProfileConsents, updateProfileConsents } from '@/services/platform/data/profile/consents';
import { ProfileSettingsNotifications } from '@/components/shared/profile/settings/notifications';
import { InformativeSheet } from '@/components/shared/sheets/informative';

export const ManageNotificationsApi = (props: ModuleProperties) => {
  const content = props.content as ManageNotificationsApiElementContentModel | undefined;
  const contentProperties = content?.properties;
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  const navigate = useNavigate();

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  const [notificationTypes, setNotificationTypes] = useState<NotificationTypeContentResponseModel[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferenceDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disabledSheetOpen, setDisabledSheetOpen] = useState(false);
  const [pushDisabledSheetOpen, setPushDisabledSheetOpen] = useState(false);
  const [pendingPushDisabledSheet, setPendingPushDisabledSheet] = useState(false);

  useEffect(() => {
    const raw = contentProperties?.notificationTypes as NotificationTypeContentResponseModel[] | undefined;
    if (!raw?.length) return;

    getNotificationTypes(raw, locale).then(setNotificationTypes);
  }, [contentProperties?.notificationTypes, locale]);

  useEffect(() => {
    async function load() {
      const [notifications, consents] = await Promise.all([getProfileNotifications(), getProfileConsents()]);
      if (notifications) setPreferences(notifications);
      const anyEnabled =
        (notifications ?? []).some(p => p.isEnabled) && (consents ?? []).some(c => c.isOptedIn);

      const pushSkipped = Capacitor.isNativePlatform()
        ? await pushNotificationIsSkippedOrNotAllowed()
        : false;

      if (!anyEnabled) {
        setDisabledSheetOpen(true);
        if (pushSkipped) setPendingPushDisabledSheet(true);
      } else if (pushSkipped) {
        setPushDisabledSheetOpen(true);
      }

      setLoading(false);
    }
    load();
  }, []);

  function handleDisabledSheetOpenChange(open: boolean) {
    setDisabledSheetOpen(open);
    if (!open && pendingPushDisabledSheet) {
      setPushDisabledSheetOpen(true);
      setPendingPushDisabledSheet(false);
    }
  }

  async function handleSave(notifications: UpdateNotificationPreferenceRequest[]) {
    setSaving(true);
    await updateProfileNotifications({ notifications });
    setSaving(false);
  }

  if (!content) return null;

  const goToLink = LinksToLinkMap(contentProperties?.infoModalGoToBtnText ?? undefined);
  const goToLabel = goToLink?.label ?? undefined;
  const privacyPolicyPath = props.context?.settings?.properties?.privacyPolicy?.route?.path;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__manage-notifications']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ProfileSettingsNotifications
        subTitle={contentProperties?.subTitle}
        notificationTypes={notificationTypes}
        preferences={preferences}
        loading={loading}
        saving={saving}
        onSave={handleSave}
        onPrivacyPolicy={privacyPolicyPath ? () => navigate(privacyPolicyPath) : undefined}
        cookiesModal={{
          title: contentProperties?.cookiesModalTitle,
          descriptionText: contentProperties?.cookiesModalDescription,
          confirmLabel: contentProperties?.cookiesAcceptConfirmBtn,
          cancelLabel: contentProperties?.cookiesAcceptCancelBtn,
        }}
        onConsentDependenciesConfirmed={type => {
          const deps = type.properties?.consentsDependencies ?? [];
          updateProfileConsents({
            consents: deps.map(d => ({ consentTypeNodeKey: d.id, isOptedIn: true })),
          });
          updateProfileNotifications({
            notifications: [{ notificationTypeNodeKey: type.id, isEnabled: true }],
          });
        }}
      />
      <InformativeSheet
        open={disabledSheetOpen}
        onOpenChange={handleDisabledSheetOpenChange}
        onConfirm={async () => {
          await enableAllNotificationCookies(locale, props?.context?.settings);
          if (goToLink?.href) navigate(goToLink.href);
        }}
        title={contentProperties?.infoModalTitle ?? undefined}
        description={contentProperties?.infoModalDescription ?? undefined}
        confirmLabel={goToLabel ?? undefined}
        cancelLabel={contentProperties?.infoModalCancelBtnText ?? undefined}
      />
      <InformativeSheet
        open={pushDisabledSheetOpen}
        onOpenChange={setPushDisabledSheetOpen}
        onConfirm={requestPushNotificationPermission}
        title={t('App.Labels.PhoneNotificationsDisabledTitle')}
        descriptionText={t('App.Labels.PhoneNotificationsDisabledDescription')}
        confirmLabel={t('App.Labels.ActivateNotifications')}
        cancelLabel={t('App.Labels.MaybeLater')}
      />
    </ModuleWrapper>
  );
};
